import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUserPermission from './useUserPermission';
import {
  initializeLanguage,
  getStoredLanguageOrNull,
  getBrowserLanguage,
  isManualSwitch,
  clearManualSwitchFlag,
  SupportedLanguage,
} from '@/i18n';
import { isFeishuEnv } from '@/utils/feishu';

/**
 * 语言初始化 Hook
 * 实现语言优先级：手动切换 > 飞书环境中文 > 本地缓存（手动设置）> 接口返回 > 浏览器语言 > 默认英文
 */
const useLanguageInitialization = () => {
  const { i18n } = useTranslation();
  const { apiLanguage, isLanguageLoaded } = useUserPermission();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // 只有在 API 加载完成且还没初始化时执行
    if (isLanguageLoaded && !hasInitialized.current) {
      console.log('初始化语言设置:', apiLanguage, '当前语言:', i18n.language);

      // 检查是否是手动切换后的刷新
      if (isManualSwitch()) {
        console.log('检测到手动切换标识，以手动设置为准');
        clearManualSwitchFlag();
        hasInitialized.current = true;
        return;
      }

      const storedLanguage = getStoredLanguageOrNull();
      let targetLanguage: SupportedLanguage;
      let shouldPersist = false;

      // 优先级：飞书环境中文 > 本地缓存 > 接口返回 > 浏览器语言
      if (isFeishuEnv()) {
        targetLanguage = 'zh-CN';
        shouldPersist = storedLanguage !== 'zh-CN';
        console.log('检测到飞书环境，初始化语言固定为中文:', targetLanguage);
      } else if (storedLanguage) {
        targetLanguage = storedLanguage;
        console.log('使用本地缓存的语言:', targetLanguage);
      } else if (apiLanguage) {
        targetLanguage = apiLanguage;
        shouldPersist = true;
        console.log('使用接口返回的语言:', targetLanguage);
      } else {
        targetLanguage = getBrowserLanguage();
        console.log('使用浏览器语言:', targetLanguage);
      }

      if (targetLanguage !== i18n.language) {
        console.log('设置语言到:', targetLanguage);
        initializeLanguage(targetLanguage, { persist: shouldPersist });
      } else {
        console.log('当前语言已经是目标语言:', i18n.language);
      }

      hasInitialized.current = true;
    }
  }, [isLanguageLoaded, apiLanguage, i18n]);

  return { currentLanguage: i18n.language, apiLanguage };
};

export default useLanguageInitialization;
